import { call, put, takeEvery, select, delay } from 'redux-saga/effects';
import { find } from 'lodash';
import { getLocation, matchPath } from 'connected-react-router';
import { actions, constants } from './redux';
import { getEvents as getTimeplanEvents } from '../../../timeplans/selector';
import Api from '../../../../utils/Api';
import { addItem as notify } from '../../../notifications/actions';
import {commentData, commentEventData, isUserDataChanged, userData} from './utils';
import { addError, addSuccess } from '../../../notifications/actions';
import apiErrorMessage from '../../../../utils/Api/apiErrorMessage';

export function* fetchSeaService() {
  try {
    const userId = yield select((s) => s.seafarersView.userId);
    // 2026-05-21: was `itemsPerPage: 12, current: 1` — capped
    // the inline Sea Service panel at the first 12 contracts.
    // Seafarers with longer careers (and the migration data
    // set has plenty) silently lost the tail. The panel
    // doesn't paginate itself — it's an at-a-glance inline
    // history — so just ask for a sane upper bound. 500 is
    // well over any realistic per-seafarer count; the API's
    // JSON:API serialiser short-circuits if the collection is
    // smaller.
    const payload = {
      userId,
      sort: { field: 'date_on', direction: 'desc' },
      pagination: { itemsPerPage: 500, current: 1 },
    };
    const { data: { data } } = yield call(Api.userExperiences.index, payload);
    yield put(actions.setSeaService(data));
  } catch (error) {
    console.error(error);
    yield put(actions.setSeaServiceErrors(error));
  }
}

export function* fetchComments() {
  try {
    const isUsers = window.location.pathname.includes('users');
    const isUserDetails = window.location.pathname.includes('comments');
    const userId = yield select((s) => isUserDetails ? s.userDetailsPage.data.id : s.seafarersView.userId);
    const c_filters = yield select((s) => s.comments.filter);
    const s_filters = yield select((s) => s.seafarersView.comments.filters);
    const { id, resource, ...filters } = { id: userId, resource: 'users', ...s_filters, ...(isUsers ? {} : c_filters) };
    const { data } = yield call(Api.comments.index, resource, id, filters);
    yield put(actions.setComments(data));
  } catch (error) {
    console.error(error);
    yield put(actions.setCommentsErrors(error));
  }
}

export function* assignTagMulti({ payload: { tag, userId } }) {
  try {
    const featureState = yield select((s) => s.users);

    yield call(Api.users.assignTag, { ...featureState, tag, userId });
    yield put(notify({
      type: 'success',
      title: 'Success',
      message: 'The task was successfully added to the queue. This may take a little time.',
    }));
    yield put({ type: '@USERS/LOAD_ITEMS_REQUEST' });
    yield put(actions.assignTagMultiRequest(false));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

export function* removeTagMulti({ payload: { tag, userId } }) {
  try {
    const featureState = yield select((s) => s.users);

    yield call(Api.users.removeTag, { ...featureState, tag, userId });
    yield put(notify({
      type: 'success',
      title: 'Success',
      message: 'The task was successfully added to the queue. This may take a little time.',
    }));
    yield put({ type: '@USERS/LOAD_ITEMS_REQUEST' });
    yield put(actions.removeTagMultiRequest(false));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

// CREW-1283 — the seafarer's change (a debriefing's new status, and with it the
// owner's pool) is written BEFORE the comment. It used to come last: a refused
// status left the debriefing written and the seafarer in "Pending debriefing",
// so the coordinator wrote it again. `cb(ok)` lets the form keep what was typed
// when anything is refused.
export function* createCommentEvent({ payload: {form, cb} }) {
  let ok = false;
  try {
    const currentUser = yield select((s) => s.auth.data);
    // When the comment is being created from a timeplan slot context
    // (`form.resource === 'time_plans'`), the saga needs the slot's
    // `userId` (the seafarer pinned to that timeplan, not the comment
    // author). Pre-Epic-13f this came from
    // `state.form.timeplans.values.userId` — the redux-form slice.
    // After the RHF migration `state.form` is gone, so the old read
    // throws `Cannot read properties of undefined (reading 'timeplans')`
    // and the saga aborts mid-comment-submit (QA bug 2.3 echo,
    // 2026-05-19). Walk the events list (which joins timeplan rows
    // with their manning-scale role info) and pluck the row out by
    // the id in the URL — same source the RHF form's selector uses.
    let userId = form.userId;
    if (form.resource === 'time_plans') {
      const { pathname } = yield select(getLocation);
      const editMatch    = matchPath(pathname, { path: '/(timeplans|vacancies)/:id/edit' });
      const editId       = editMatch?.params?.id;
      const events       = yield select(getTimeplanEvents);
      userId = (editId && find(events, { id: editId })?.userId) || null;
    }

    const userChanged = isUserDataChanged(form);
    if (userChanged) yield call(Api.users.update, userId, userData(form));

    const comment = yield call(Api.comments.createFull, commentData(form, currentUser.id));
    const { data: { data: commentEvent } } = yield call(Api.commentEvents.create, commentEventData(form, comment.id));

    yield put(actions.addNewCommentRequest({ show: false }));
    if (userChanged) {
      yield delay(200);
      yield put({ type: '@USERS/LOAD_ITEMS_REQUEST' });
      yield put(addSuccess('Interaction successfull.'));
    }
    if (form.file) {
      yield call(Api.userAttachments.uploadAttachment, form.file.name, currentUser.id,
        '', form.file.localUrl, commentEvent.id, 'CommentEvent');
    }
    yield delay(200);
    yield put(actions.addNewCommentSuccess());
    yield fetchComments();
    ok = true;
  } catch (e) {
    yield put(addError(apiErrorMessage(e), 'Updating failed.'));
    console.error(e);
  } finally {
    yield call(cb, ok);
  }
}

export function* starUser({ payload: {userId, isDetailsPage} }){
  try{
    yield call(Api.favorites.starUser, userId);
    const type = isDetailsPage ? '@USER_DETAILS_PAGE/LOAD_ITEM_REQUEST' : '@USERS/LOAD_ITEMS_REQUEST';
    yield put({ type, payload: userId });
  } catch (e) {
    console.error(e);
  }
}

export default () => [
  takeEvery([
    constants.FETCH_SEA_SERVICE_REQUEST,
  ], fetchSeaService),
  takeEvery([
    constants.FETCH_COMMENTS_REQUEST,
  ], fetchComments),
  takeEvery([
    constants.ASSIGN_TAG_MULTI,
  ], assignTagMulti),
  takeEvery([
    constants.REMOVE_TAG_MULTI,
  ], removeTagMulti),
  takeEvery([
    constants.ADD_NEW_COMMENT,
  ], createCommentEvent),
  takeEvery([
    constants.STAR_USER,
  ],starUser),
];
